/* ---------------------------------------------------------------------------
   CUSTOM SCALE FIGURE  —  assets/custom-scale-figure.js
   ---------------------------------------------------------------------------
   Draws a true-to-scale museum figure: the replica at the selected size next
   to everyday objects, or next to the real fossil skull and an adult human.

   Everything inside the SVG is measured in INCHES — the viewBox is literally
   inches across. That means every stroke width and font size has to be derived
   from the viewBox width (U), or it renders at absurd sizes. That was the one
   real trap in building this.

   The frame width is FIXED per mode and never rescales between sizes. If it
   rescaled, the drawing would look identical at 5" and 13" and the whole point
   of the section would be lost.
   --------------------------------------------------------------------------- */

(function () {
  'use strict';

  var CM_PER_IN = 2.54;
  var HUMAN_W = 0.268;   // the figure's viewBox is 268 wide x 1000 tall

  function esc(str) {
    return String(str).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function CSF(root) {
    this.root = root;
    var cfgEl = root.querySelector('[data-csf-config]');
    if (!cfgEl) return;
    try {
      this.cfg = JSON.parse(cfgEl.textContent);
    } catch (e) {
      return;
    }
    if (!this.cfg.sizes || !this.cfg.sizes.length) return;

    this.stage = root.querySelector('[data-csf-stage]');
    this.footL = root.querySelector('[data-csf-foot-left]');
    this.sizeBtns = Array.prototype.slice.call(root.querySelectorAll('[data-csf-size]'));
    this.modeBtns = Array.prototype.slice.call(root.querySelectorAll('[data-csf-mode]'));
    this.out = {
      len: root.querySelector('[data-csf-out-length]'),
      cm: root.querySelector('[data-csf-out-cm]'),
      ratio: root.querySelector('[data-csf-out-ratio]'),
      price: root.querySelector('[data-csf-out-price]')
    };
    this.cta = root.querySelector('[data-csf-cta]');

    this.cur = Math.min(this.cfg.defaultIndex || 0, this.cfg.sizes.length - 1);
    this.mode = this.modeBtns.length ? this.modeBtns[0].getAttribute('data-csf-mode') : 'shelf';

    this.bind();
    this.render();
  }

  CSF.prototype.bind = function () {
    var self = this;
    this.sizeBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        self.cur = parseInt(btn.getAttribute('data-csf-size'), 10) || 0;
        self.render();
      });
    });
    this.modeBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        self.mode = btn.getAttribute('data-csf-mode');
        self.render();
      });
    });

    /* The annotation scale depends on how many CSS pixels one inch of the
       drawing occupies, so the figure has to be redrawn when it is resized. */
    var t;
    var redraw = function () {
      clearTimeout(t);
      t = setTimeout(function () { self.render(); }, 120);
    };
    window.addEventListener('resize', redraw);
    if (typeof ResizeObserver === 'function') {
      new ResizeObserver(redraw).observe(this.stage);
    }
  };

  /* One inch of drawing is only a couple of CSS pixels wide on a phone, so a
     label sized as a flat fraction of the viewBox comes out at ~4px and is
     unreadable. Inflate the whole annotation scale — type, ticks and the gaps
     between them together — until the smallest text clears MIN_LABEL_PX. */
  CSF.prototype.unit = function (W) {
    var MIN_LABEL_PX = 9.5;
    var design = W / 100;
    var px = this.stage.getBoundingClientRect().width;
    if (!px) return design;
    var perUnit = px / W;
    return Math.max(design, MIN_LABEL_PX / 1.6 / perUnit);
  };

  /* ---- drawing helpers. every size is in inches; U scales type + strokes ---- */

  CSF.prototype.skull = function (x, base, lenIn, color, opacity) {
    var h = lenIn * this.cfg.skullRatio;
    var k = lenIn / 1000;
    return (
      '<use href="#' + this.cfg.symbolId + '" transform="translate(' + x + ' ' +
      (base - h) + ') scale(' + k + ')" color="' + color + '" opacity="' + opacity + '"/>'
    );
  };

  CSF.prototype.label = function (x, base, text, U, strong) {
    return (
      '<text class="csf-lbl' + (strong ? ' csf-lbl--strong' : '') + '" x="' + x +
      '" y="' + (base + U * 1.9) + '" text-anchor="middle" font-size="' + U * 1.6 + '">' +
      esc(text) + '</text>'
    );
  };

  CSF.prototype.ruler = function (x0, x1, base, step, U) {
    var sw = U * 0.16;
    var s = '<line class="csf-rule-line" x1="' + x0 + '" y1="' + base + '" x2="' + x1 +
      '" y2="' + base + '" stroke-width="' + sw + '"/>';
    for (var i = Math.ceil(x0); i <= x1; i += step) {
      var major = i % (step * 5) === 0;
      s += '<line class="csf-tick" x1="' + i + '" y1="' + base + '" x2="' + i + '" y2="' +
        (base + (major ? U * 1.5 : U * 0.8)) + '" stroke-width="' + sw + '"/>';
      if (major && i > 0) {
        s += '<text class="csf-lbl" x="' + i + '" y="' + (base + U * 3.6) +
          '" text-anchor="middle" font-size="' + U * 1.55 + '">' + i + '&#8243;</text>';
      }
    }
    return s;
  };

  /* Built from an anthropometric ratio table (scratchpad/human.py), not by eye:
     head 13% of stature, legs 48%, shoulders 23%, fingertips at 38%. The
     freehand version this replaces read as far too tall because its legs were
     53% of height and its shoulders only 12%. viewBox is 268 x 1000. */
  CSF.prototype.human = function (x, base, hIn) {
    var k = hIn / 1000;
    return (
      '<g transform="translate(' + x + ' ' + (base - hIn) + ') scale(' + k +
      ')" fill="' + this.cfg.ghostColor + '" opacity="' + this.cfg.ghostOpacity + '">' +
      '<ellipse cx="134" cy="65" rx="47" ry="65"/><path d="M 108,126 L 160,126 C 166,160 227,174 249,192 L 229,270 L 209,400 L 224,480 L 214,520 L 54,520 L 44,480 L 59,400 L 39,270 L 19,192 C 41,174 102,160 108,126 Z"/><path d="M 128,520 L 48,514 C 52,660 79,690 79,720 C 79,790 96,895 104,955 L 41,992 C 35,1000 120,1000 122,990 L 120,955 L 118,720 L 128,520 Z"/><path d="M 140,520 L 220,514 C 216,660 189,690 189,720 C 189,790 172,895 164,955 L 227,992 C 233,1000 148,1000 146,990 L 148,955 L 150,720 L 140,520 Z"/><path d="M 42,188 C 2,192 -8,228 -5.5,370 C -5,505 -3,545 -1,606 C 3,626 31,626 34,604 C 37,545 35.8,400 37,370 C 39.5,272 42,212 42,188 Z"/><path d="M 226,188 C 266,192 276,228 273.5,370 C 273,505 271,545 269,606 C 265,626 237,626 234,604 C 231,545 232.2,400 231,370 C 228.5,272 226,212 226,188 Z"/>' +
      '</g>'
    );
  };

  CSF.prototype.mug = function (x, base, U) {
    var h = this.cfg.mugHeight;
    var w = h * 0.8;
    return (
      '<g fill="' + this.cfg.ghostColor + '" opacity="' + this.cfg.ghostOpacity + '">' +
      '<path d="M' + x + ' ' + (base - h) + ' h' + w + ' v' + h + ' h-' + w + ' z"/>' +
      '<path fill="none" stroke="' + this.cfg.ghostColor + '" stroke-width="' + U * 0.34 +
      '" d="M' + (x + w) + ' ' + (base - h + h * 0.19) + ' a' + h * 0.31 + ' ' + h * 0.33 +
      ' 0 0 1 0 ' + h * 0.64 + '"/></g>'
    );
  };

  CSF.prototype.book = function (x, base, U) {
    var h = this.cfg.bookHeight;
    var w = this.cfg.bookWidth;
    return (
      '<g opacity="' + this.cfg.ghostOpacity + '">' +
      '<path fill="' + this.cfg.ghostColor + '" d="M' + x + ' ' + (base - h) + ' h' + w +
      ' v' + h + ' h-' + w + ' z"/>' +
      '<line x1="' + (x + w * 0.16) + '" y1="' + (base - h) + '" x2="' + (x + w * 0.16) +
      '" y2="' + base + '" stroke="' + this.cfg.figureBg + '" stroke-width="' + U * 0.3 +
      '"/></g>'
    );
  };

  /* ---- render ---- */

  CSF.prototype.render = function () {
    var cfg = this.cfg;
    var S = cfg.sizes[this.cur];
    var self = this;

    this.sizeBtns.forEach(function (b) {
      b.setAttribute('aria-pressed', String(parseInt(b.getAttribute('data-csf-size'), 10) === self.cur));
    });
    this.modeBtns.forEach(function (b) {
      b.setAttribute('aria-pressed', String(b.getAttribute('data-csf-mode') === self.mode));
    });

    if (this.out.len) this.out.len.textContent = S.label;
    if (this.out.cm) this.out.cm.textContent = (S.inches * CM_PER_IN).toFixed(1) + ' cm';
    if (this.out.ratio) this.out.ratio.textContent = S.ratio || '—';
    if (this.out.price) this.out.price.textContent = S.price || '';
    if (this.cta && S.url) this.cta.setAttribute('href', S.url);

    var svg = '';
    var W;
    var U;
    var base;
    var maxIn = cfg.sizes.reduce(function (m, s) { return Math.max(m, s.inches); }, 0);

    if (this.mode === 'shelf') {
      var refW = (cfg.showMug ? cfg.mugHeight * 0.8 + 1.6 : 0) +
                 (cfg.showBook ? cfg.bookWidth + 2.4 : 0);
      W = Math.max(refW + maxIn + 3.5, 12);
      U = this.unit(W);
      base = Math.max(maxIn * cfg.skullRatio, cfg.bookHeight, cfg.mugHeight) + 1.8;

      var x = 1.0;
      if (cfg.showMug) {
        svg += this.mug(x, base, U) + this.label(x + cfg.mugHeight * 0.4, base, cfg.mugLabel, U);
        x += cfg.mugHeight * 0.8 + 1.6;
      }
      if (cfg.showBook) {
        svg += this.book(x, base, U) + this.label(x + cfg.bookWidth / 2, base, cfg.bookLabel, U);
        x += cfg.bookWidth + 2.4;
      }
      svg += this.skull(x, base, S.inches, cfg.skullColor, 1);
      svg += this.label(x + S.inches / 2, base, S.label + ' ' + cfg.replicaWord, U, true);
      svg += this.ruler(0, W, base + U * 3.4, 1, U);
      if (this.footL) this.footL.textContent = cfg.shelfFootnote;
    } else {
      var humanW = cfg.humanHeight * HUMAN_W;
      W = humanW + 6 + cfg.fossilLength + 4.5 + maxIn + 2;
      U = this.unit(W);
      base = Math.max(cfg.humanHeight, cfg.fossilLength * cfg.skullRatio) + 3;

      svg += this.human(1, base, cfg.humanHeight);
      svg += this.label(1 + humanW / 2, base, cfg.humanLabel, U);

      var fx = humanW + 6;
      svg += this.skull(fx, base, cfg.fossilLength, cfg.ghostColor, cfg.ghostOpacity);
      svg += this.label(fx + cfg.fossilLength / 2, base, cfg.fossilLabel, U);

      var rx = fx + cfg.fossilLength + 4.5;
      svg += this.skull(rx, base, S.inches, cfg.skullColor, 1);
      svg += this.label(rx + S.inches / 2, base, S.label + ' ' + cfg.replicaWord, U, true);
      svg += this.ruler(0, W, base + U * 3.4, cfg.fossilRulerStep, U);
      if (this.footL) this.footL.textContent = cfg.fossilFootnote;
    }

    var H = base + U * 11;
    this.stage.setAttribute('viewBox', '0 0 ' + W + ' ' + H);
    this.stage.innerHTML = svg;
  };

  function initAll(scope) {
    var nodes = (scope || document).querySelectorAll('[data-csf]');
    Array.prototype.forEach.call(nodes, function (n) {
      if (n.dataset.csfReady === 'true') return;
      n.dataset.csfReady = 'true';
      new CSF(n);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  /* theme editor */
  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
  document.addEventListener('shopify:section:unload', function (e) {
    var n = e.target.querySelector('[data-csf]');
    if (n) n.dataset.csfReady = 'false';
  });
})();
